import React from "react";
import { html } from "lit-html";
import * as XLSX from 'xlsx';
import parse from 'html-react-parser';

// Shared components and utilities
import { 
    OmniIconComponent, 
    OmniSearchComponent, 
    OmniTableStickyComponent, 
    OmniTileComponent, 
    OmniToolbarComponent, 
    OmniIconMenu,
    LoaderComponent, 
    AlertMessagesService, 
    Utils, 
    NoDataComponent 
} from '../../../../shared';

// Feature imports
import { RatesAndFeesService } from '../../';
export class RatesAndFeesComponent extends React.Component {

    data;

    constructor(props) {
        super(props);
        this.state = {
            formData: {},
            loaded: false,
            rerender: false,
            disableForm: true,
            disableCancel: true,
            activeTabData: [],
            activeTab: 'dsp',
            isAlertOpen: false,
            deleteData: {},
            deleteIndex : -1,
            data: [],
            dropdownOptions: {}
        }
    }

    componentDidMount() {
        this.createDropdownOptions();
        this.createDspFormData();
        this.addFileUploadEventHandler();
        window?.addEventListener('resize', this.ontableResize);

    }

    importExportOption = () => {
        return ["Import data", "Export data"];
    }

    /**
     * The below code is checking if the values in the formData object match the values in the data array
     * for each corresponding key. It iterates over each key in the formData object and then iterates over
     * each index in the data array. If any value in the formData object does not match the corresponding
     * value in the data array, it returns false. If all values match, it returns true.
     * @param {Object} formData 
     * @returns {Boolean}
     */
    isFormUpdated = (index) => {
        const ignoredKeys = ['id', 'isEdit', 'agencyId', 'clientId', 'ratesAndFeesId', 'createdBy', 'updatedBy', 'dataInFee'];
        const keys = Object.keys(this.state.formData["row" + index]);
        for (const key of keys) {
            if(!ignoredKeys.includes(key)){
                if(!this.state.formData["row" + index][key]) return;
            }
        }
        this.setState({disableForm: false, disableCancel: false});
    }

    /**
     * The below code has if statement that checks if the `event.value` is truthy (not null or undefined). 
     * If it is truthy, the code will update the values of a dropdown menu in a form. 
     * @param {String} row 
     * @param {Number} index 
     * @param {String} dropdownTitle 
     * @param {Object} event 
     */
    onDropDownChange = (row, index, dropdownTitle, event) => {
        if (event?.value) {
            const formData = JSON.parse(JSON.stringify(this.state.formData));
            let isEdit = false;
            formData['row' + index][dropdownTitle] = event?.value;
            if(formData['row' + index][dropdownTitle] !== this.state.formData['row' + index][dropdownTitle]){
                isEdit = true;
                formData['row' + index]['isEdit'] = true;
            }
            if(dropdownTitle === 'clkRateType' && !['CPC', 'dCPC'].includes(formData['row'+index][dropdownTitle])){
                formData['row'+index]['estimatedClicks'] = 'NA';
            }
            this.addNewAdServerColumn(dropdownTitle, event?.value, formData);
            this.setState({ formData: formData}, ()=>{
                this.isFormUpdated(index);
            });
        }
    }

    /**
     * Adds a new column for the ad server dropdown with the options 'Clicks', 'Impressions', and 'NA'.
     * The column is added to the 'adServerLeftColumns' array based on certain conditions.
     * @param {string} dropdownTitle - The title of the dropdown, e.g., 'clkRateType'.
     * @param {string} dValue - The value of the dropdown.
     * @param {Object} formData - The form data object containing the row data.
     * @param {boolean} [isLoaded=true] - A flag indicating if the form data is loaded or not.
     */
    addNewAdServerColumn = (dropdownTitle, dValue, formData, isLoaded = true) => {
          // Define the options for the dropdown
        const options = [{label: 'Clicks', value: 'Clicks'}, {label: 'Impressions', value: 'Impressions'},{label: 'NA', value: 'NA'}];
        if(dropdownTitle === 'clkRateType') {
                // Define the object for the 'estimatedClicks' column
            const object = {
                label: 'ESTIMATED CLICKS',
                key: 'estimatedClicks',
                isSortable: true,
                template: (value, index) => {
                        // Check if the value in the 'dropdownTitle' column is 'CPC' or 'dCPC'
                    const disabled = ['CPC', 'dCPC'].includes(formData['row'+index][dropdownTitle]);
                            // Initialize the 'dropdownValue' with the value from the state
                    let dropdownValue = [this.state.formData['row'+index]['estimatedClicks']];
                            // If the value is not 'CPC' or 'dCPC' and not 'NA', set 'dropdownValue' to 'NA'
                    if(!disabled && formData['row'+index]['estimatedClicks'] !== 'NA'){
                        dropdownValue = ['NA'];
                    }
                    return html`
                    <style>
                    :host::part(dynamic-column) {
                        opacity: 0.5;
                        cursor: not-allowed;
                        pointer-events: none;
                    }
                    </style>
                    <td>
                    <omni-dropdown
                    part=${disabled ? '' : 'dynamic-column'}
                    hidefooter
                    typeahead
                    searchindropdown
                    variant="label"
                    placeholder="Select"
                    style="min-width: 100px; max-width: 150px;"
                    .options=${options}
                    .value=${dropdownValue}
                    @change=${({ detail }) => this.onDropDownChange(value, index, 'estimatedClicks', detail)}
                    />
                    </td>` 
                }
            };
            if(dValue && ['CPC', 'dCPC'].includes(dValue)) {
                if(!this.adServerLeftColumns.some(item => item.key === 'estimatedClicks')){
                    this.adServerLeftColumns.splice(6, 0, object);
                }else{
                    this.adServerLeftColumns = this.adServerLeftColumns.map(item => {
                        if(item.key === 'estimatedClicks'){
                            return object;
                        }
                        return item;
                    });
                }
            } else if (isLoaded && !Object.values(formData).some(row => ['CPC', 'dCPC'].includes(row.clkRateType))){
                // If 'isLoaded' is true and none of the rows in 'formData' have 'CPC' or 'dCPC' in the 'clkRateType' column
                // Remove the 'estimatedClicks' column from 'adServerLeftColumns'
                this.adServerLeftColumns = this.adServerLeftColumns.filter(item => item.key !== 'estimatedClicks');
            }else if (dValue && !['CPC', 'dCPC'].includes(dValue)) {
                // If 'dValue' is not 'CPC' or 'dCPC' and the 'estimatedClicks' column is not present
                // Add the 'object' to 'adServerLeftColumns'
                this.adServerLeftColumns = this.adServerLeftColumns.map(item => {
                    if(item.key === 'estimatedClicks'){
                        return object;
                    }
                    return item;
                });
            }
        }
    };
    
    /**
     * The below code is creating a table cell (`<td>`) with a dropdown menu (`<omni-dropdown>`) inside it.
     * The dropdown menu is populated with options from the `value[dropdownTitle + 'Options']` array. The
     * selected value of the dropdown menu is set to `value[dropdownTitle]`. When the dropdown menu value
     * is changed, the `onDropDownChange` function is called with the `value`, `index`, `dropdownTitle`,
     * and `detail` as arguments.
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} dropdownTitle 
     * @returns 
     */
    getDropdownTemplate = (value, index, dropdownTitle, dropDownOption) => {
        return html`
        <style>
            :host::part(target) {
                padding: 10px !important;
            }
            :host td{
                overflow: revert !important;
            }
        </style>
        <td>
            <omni-dropdown
            style="min-width: 100px; max-width: 150px;"
            typeahead
            searchindropdown
            hidefooter
            searchPlaceholder="Select"
            .value=${[this.state.formData['row'+index][dropdownTitle]]}
            .options=${this.state.dropdownOptions[(dropDownOption ? dropDownOption : dropdownTitle) + 'Options']} 
            @change=${({ detail }) => this.onDropDownChange(value, index, dropdownTitle, detail)}>
            </omni-dropdown>
        </td>`
    }

    // TODO: NEED TO WORK MORE ON DROPDOWN OPTIONS
    reAlignDropdownList = (event) => {
        let options = event.target.options;
        if(event.detail){
            setTimeout(() => {
                let dropdown = document.getElementById('ratesAndFeesTable').shadowRoot.querySelector('#left-table').shadowRoot.querySelectorAll('omni-dropdown')[4].shadowRoot.querySelectorAll('div[part="dropdown"]')[0];
                dropdown = dropdown.parentNode.removeChild(dropdown);
                let newParent = document.getElementsByClassName('table-section')[0];
                
                newParent.insertAdjacentHTML('afterend', this.formDropDown(options));
            });
        }
    }

    // TODO: NEED TO WORK MORE ON DROPDOWN OPTIONS
    formDropDown = (options) => {
        return html `
            <style>
            .item {
                min-height: 2.57143rem;
                padding: 0.571429rem 0.571429rem 0.571429rem 0px;
                margin: 0px;
                user-select: none;
                display: flex;
                flex-grow: 1;
                align-items: flex-start;
                justify-content: flex-start;
                border: 0.0714286rem solid transparent;
            }
            </style>
            <div>
                ${options.map((option) => html`
                    <div class='item' tabIndex="0">
                        ${option}
                    </div>
                `)}
            </div>
        `
    }

    /**
     * The below code is updating the value of a specific field in a form data object. 
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @param {Object} event 
     */
    updateValueInFormdata = (value, index, field, event) => {
        const formData = JSON.parse(JSON.stringify(this.state.formData));
        formData['row' + index][field] = event?.target?.value;
        let isEdit = false;
        if(formData['row' + index][field] !== this.state.formData['row' + index][field]){
            isEdit = true;
            formData['row' + index]['isEdit'] = true;
        }
        this.setState({ formData: formData}, () => {
            this.isFormUpdated(index);
        })
    }

    /**
     * The below code is defining a JavaScript function that returns an HTML template. The template is
     * creating an input field within a table cell. The input field has an id that is dynamically generated
     * based on the values of the "field" and "index" variables. It also has a "part" attribute set to
     * "cell-input" and a class set to "input is-static is-editable". The input field is of type "text" and
     * its initial value is set to the value variable. Additionally, there is an event listener attached to
     * the input field's "blur" event, which calls the updateValueInFormdata()
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @returns 
     */
    getTextboxTemplate = (value, index, field) => {
        return html`<td><input id=${field + '-' + index}
        style="min-width: 150px;padding-left: 10px;font-weight: 600;"
        part="cell-input ${field === 'platformFee' ? 'platform-fee' : ''}"
        class="input is-static is-editable"
        type="text"
        .value=${value || ''}
        @change=${(event) => this.updateValueInFormdata(value, index, field, event)}></td>`;
    }

    /**
     * The below code is defining a JavaScript function that returns an HTML template. The template is
     * creating an input field within a table cell. The input field has an id that is dynamically generated
     * based on the values of the "field" and "index" variables.
     * @param {Object} value
     * @param {Number} index
     * @param {String} field
     * @returns
     */
    getNumberTemplate = (value, index, field) => {
        return html`
        <style>
            input[type=number]::-webkit-inner-spin-button, 
            input[type=number]::-webkit-outer-spin-button { 
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                margin: 0; 
            }
        </style>
        <td>
            <input id=${field + '-' + index}
            type="number"
            min="0"
            style="min-width: 150px;padding-left: 10px;font-weight: 600;"
            part="cell-input ${field === 'platformFee' ? 'platform-fee' : ''}"
            class="input is-static is-editable"
            type="text"
            .value=${value || ''}
            @keydown=${this.blockInvalidChar}
            @change=${(event) => this.updateValueInFormdata(value, index, field, event)}>
        </td>`;
    }

    /**
     * Function to check for the invalid characters in the input field
     * @param {Object} e - Event object
     * @returns {void}
     */ 
    blockInvalidChar = e => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

    /**
     * The below code is updating the values of a form data object and a mock data object based on the
     * selected value. It is using the index and field parameters to specify which value to update. After
     * updating the values, it is updating the state of the component with the new form data and mock data.
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @param {String} selectedValue 
     * @param {Object} event 
     */
    onRadioValueChange = (value, index, field, selectedValue, event) => {
        const formData = {...this.state.formData};
        formData['row' + index][field] = selectedValue;
        this.setState({ formData: formData}, ()=>{
            this.isFormUpdated(index);
        });
    }

    /**
     * The below code is creating a table cell (`<td>`) with two radio buttons (`<input type="radio">`)
     * labeled "Y" and "N". The radio buttons are associated with a specific field and index, and their
     * checked state is determined by the value of the corresponding field in the `value` object. When a
     * radio button is selected, the `onRadioValueChange` function is called with the `value`, `index`,
     * `field`, and the new value as arguments. 
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @returns 
     */
    getRadioTemplate = (value, index, field) => {
        return html`
        <style>
            :host::part(data-fee-radio) {
                margin-right: 10px;
            }
        </style>
        <td id=${index + '-radio'}>
            <div class="control">
                <label class="radio">
                <input part="data-fee-radio" type="radio" ?checked=${this.state.formData['row'+index][field] === true}                 
                    name=${field + '-radio-' + index} @change=${(event) => this.onRadioValueChange(value, index, field, true, event)}>Y
                </label>
                <label class="radio">
                <input part="data-fee-radio" type="radio" ?checked=${this.state.formData['row'+index][field] === false}                                                
                    name=${field + '-radio-' + index} @change=${(event) => this.onRadioValueChange(value, index, field, false, event)}>N
                </label>
            </div>
        </td>`
    }

    /**
     * The below code is creating a table cell (`<td>`) with date type input field (`<input type="date">`)
     * When a date is selected, the `updateValueInFormdata` function is called with the `value`, `index`,
     * `field`, and the new value as arguments. 
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @returns 
     */
    getDateTemplate = (value, index, field) => {
        return html `
        <style>
        input[type="date"]:before {
            content: attr(data-date);
            margin-left: 35px;
            color: black;
        }

        input[type="date"]::-webkit-datetime-edit,
        input[type="date"]::-webkit-inner-spin-button,
        input[type="date"]::-webkit-clear-button {
            display: none;  
        }

        input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            top: 6px;
            left: 15px;
            width: 18px;
            height: 18px;
            content: 'asdasd';
            color: black;
            opacity: 1;
        }    
        </style>
        <td id=${index + '-date'}>
            <div className="date-field">
                <input
                id=${field + '-date-' + index}
                data-date=${this.state.formData['row'+index][field] ? Utils.formatDateObject(this.state.formData['row'+index][field]) : "Mon DD, YYYY"}
                type="date"
                class="input text"
                @change=${(event) => this.updateValueInFormdata(value, index, field, event)}
                .value=${this.state.formData['row'+index][field]}
                />
            </div>
        </td>`
    }

    dspLeftColumns = [
        {
            label: 'DSP',
            key: 'dsp',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'dsp') }
        },
        {
            label: 'COUNTRY',
            key: 'country',
            isSortable: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'country', 'countrycode') }
        },
        {
            label: 'ACCOUNT',
            key: 'account',
            isSortable: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'account') }
        },
        {
            label: 'DSP CODE',
            key: 'dspCode',
            isSortable: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'dspCode') }
        },
        {
            label: 'BUY TYPE',
            key: 'buyType',
            isSortable: false,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'buyType', 'buytype') }
        },
        {
            label: 'PLATFORM FEE',
            key: 'platformFee',
            isSortable: true,
            template: (value, index) => { return this.getNumberTemplate(value, index, 'platformFee') }
        },
        {
            label: 'TYPE',
            key: 'type',
            isSortable: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'type', 'unittype') }
        },
        {
            label: 'DATA IN FEE',
            key: 'dataInFee',
            isSortable: true,
            template: (value, index) => { return this.getRadioTemplate(value, index, 'dataInFee') }
        },
        {
            label: 'BUY TYPE DD',
            key: 'buyTypeDD',
            isSortable: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'buyTypeDD') }
        },
        {
            label: 'BUY TYPE CODE',
            key: 'buyTypeCode',
            isSortable: true,
            template: (value, index) => {return this.getTextboxTemplate(value, index, 'buyTypeCode')}
        }
    ];

    dspRightColumns = [
        {
            label: 'EFFECTIVE DATE',
            key: 'effectiveDate',
            isSortable: true,
            template: (value, index) => {return this.getDateTemplate(value, index, 'effectiveDate')}
        },
        {
            label: 'END DATE',
            key: 'endDate',
            isSortable: true,
            template: (value, index) => {return this.getDateTemplate(value, index, 'endDate')}
        },
        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value, index) => {
                return html`
                <td part="table-body-cell">
                    <div style="display: flex;justify-content: center;">
                            <omni-tooltip-next>
                                <button slot="invoker" aria-haspopup="true" class="icon" @click=${(e) => { this.deleteRow(value, index) }} ?disabled=${!value.id}>
                                    <omni-icon class="is-size-4" icon-id="omni:interactive:delete"></omni-icon>
                                </button>
                                <div slot="content">Delete</div>
                            </omni-tooltip-next>
                    </div>
                </td>`;
            }
        }
    ]


    adServerLeftColumns = [
        {
            label: 'AD Server',
            key: 'adServer',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'adServer', 'adserver') }
        },
        {
            label: 'COUNTRY',
            key: 'country',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'country', 'countrycode') }
        },
        {
            label: 'ACCOUNT',
            key: 'account',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'account') }
        },
        {
            label: 'AD TYPE',
            key: 'adType',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'adType', 'buycategory') }
        },
        {
            label: 'IMP RATE TYPE',
            key: 'impRateType',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'impRateType', 'buymethod') }
        },
        {
            label: 'CLK RATE TYPE',
            key: 'clkRateType',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'clkRateType', 'buymethod') }
        },
        {
            label: 'CURRENCY',
            key: 'currency',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'currency', 'currencycode') }
        },
        {
            label: 'IMP RATE',
            key: 'impRate',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getNumberTemplate(value, index, 'impRate') }
        },
        {
            label: 'CLICK RATE',
            key: 'clickRate',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getNumberTemplate(value, index, 'clickRate') }
        }
    ]

    adServerRightColumns = [
        {
            label: 'EFFECTIVE DATE',
            key: 'effectiveDate',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'effectiveDate') }
        },
        {
            label: 'END DATE',
            key: 'endDate',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'endDate') }
        },
        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value, index) => {
                return html`
                <td part="table-body-cell">
                    <div style="display: flex;justify-content: center;">
                            <omni-tooltip-next>
                                <button slot="invoker" aria-haspopup="true" class="icon" @click=${(e) => { this.deleteRow(value, index) }} ?disabled=${!value.id}>
                                    <omni-icon class="is-size-4" icon-id="omni:interactive:delete"></omni-icon>
                                </button>
                                <div slot="content">Delete</div>
                            </omni-tooltip-next>
                    </div>
                </td>`;
            }
        }
    ]

    adVerificationLeftColumns = [
        {
            label: 'PARTNER',
            key: 'partner',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'partner', 'adverification') }
        },
        {
            label: 'COUNTRY',
            key: 'country',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'country', 'countrycode') }
        },
        {
            label: 'ACCOUNT',
            key: 'account',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getTextboxTemplate(value, index, 'account') }
        },
        {
            label: 'FORMAT',
            key: 'format',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'format', 'buycategory') }
        },
        {
            label: 'RATE',
            key: 'rate',
            isSortable: true,
            template: (value, index) => { return this.getNumberTemplate(value, index, 'rate') }
        }
    ]

    adVerificationRightColumns = [
        {
            label: 'EFFECTIVE DATE',
            key: 'effectiveDate',
            isSortable: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'effectiveDate') }
        },
        {
            label: 'END DATE',
            key: 'endDate',
            isSortable: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'endDate') }
        },
        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value, index) => {
                return html`
                <td part="table-body-cell">
                    <div style="display: flex;justify-content: center;">
                            <omni-tooltip-next>
                                <button slot="invoker" aria-haspopup="true" class="icon" @click=${(e) => { this.deleteRow(value, index) }} ?disabled=${!value.id}>
                                    <omni-icon class="is-size-4" icon-id="omni:interactive:delete"></omni-icon>
                                </button>
                                <div slot="content">Delete</div>
                            </omni-tooltip-next>
                    </div>
                </td>`;
            }
        }
    ]

    addRow = () => {
        let newRow = {};
        if (this.state.activeTab.toUpperCase() === "DSP") {
            newRow = this.createDSPData({});
        } else if (this.state.activeTab.toUpperCase() === "AD SERVER") {
            newRow = this.createAdServerData({});
        } else if (this.state.activeTab.toUpperCase() === "AD VERIFICATION") {
            newRow = this.createAdVerificationData({});
        }
        const activeTabData = [newRow, ...this.state.activeTabData];
        const formData = Object.assign({}, this.state.formData);
        const updatedFormData = {};
        Object.keys(formData).forEach((key, keyIndex) => {
            const index = parseInt(key.match(/\d+/)[0]);
            updatedFormData[`row${index + 1}`] = formData[key];
        });
        updatedFormData['row0'] = newRow;
        this.addNewAdServerColumn('clkRateType', this.createAdServerData({}).clkRateType, updatedFormData);
        this.setState({ activeTabData, formData: updatedFormData, disableCancel: false}, ()=>{
            setTimeout(() => {
                window?.addEventListener('resize', this.ontableResize);
            }, 10);
        });
    }

    saveEdits = () => {
        const newPayload = {
            "agency_id": 'abcd',
            "type": this.convertStringToUpperCase(this.state.activeTab, this.state.activeTab !== 'dsp'),
            "ratesandfees": []
        };

        const editPayload = {
            "type": this.convertStringToUpperCase(this.state.activeTab, this.state.activeTab !== 'dsp'),
            "ratesandfees": []
        };

        if (this.state.activeTab.toUpperCase() === "DSP") {
            Object.keys(this.state.formData).forEach((key, keyIndex) => {
                let requestBody = {
                    account: this.state.formData[key].account,
                    buy_type: this.state.formData[key].buyType,
                    buy_type_dd: this.state.formData[key].buyTypeDD,
                    code: this.state.formData[key].dspCode,
                    country: this.state.formData[key].country,
                    created_by: this.state.formData[key].id ? this.state.formData[key].createdBy : this.props.omniProfile.email,
                    data_in_fee: this.state.formData[key].dataInFee,
                    dsp: this.state.formData[key].dsp,
                    dsp_type: this.state.formData[key].type,
                    id: this.state.formData[key].id,
                    platform_fee: Number(this.state.formData[key].platformFee),
                    updated_by: this.state.formData[key].id ? this.props.omniProfile.email : this.state.formData[key].updatedBy,
                    effictive_date: this.state.formData[key].effectiveDate,
                    end_date: this.state.formData[key].endDate,
                    buy_type_code: this.state.formData[key].buyTypeCode
                }
                if(requestBody.id && this.state.formData[key].isEdit)
                    editPayload.ratesandfees.push(requestBody);
                else if(!requestBody.id)
                    newPayload.ratesandfees.push(requestBody);
            })
        } else if (this.state.activeTab.toUpperCase() === "AD SERVER") {
            Object.keys(this.state.formData).forEach((key, keyIndex) => {
                let requestBody = {
                    account: this.state.formData[key].account,
                    country: this.state.formData[key].country,
                    created_by: this.state.formData[key].id ? this.state.formData[key].createdBy : this.props.omniProfile.email,
                    id: this.state.formData[key].id,
                    updated_by: this.state.formData[key].id ? this.props.omniProfile.email : this.state.formData[key].updatedBy,
                    ad_server_name: this.state.formData[key].adServer,
                    ad_type: this.state.formData[key].adType,
                    impression_rate_type: this.state.formData[key].impRateType,
                    click_rate_type: this.state.formData[key].clkRateType,
                    fee_calc_type: this.state.formData[key].estimatedClicks,
                    currency: this.state.formData[key].currency,
                    impression_rate: this.state.formData[key].impRate,
                    click_rate: this.state.formData[key].clickRate,
                    effictive_date: this.state.formData[key].effectiveDate,
                    end_date: this.state.formData[key].endDate,
                }
                if(requestBody.id && this.state.formData[key].isEdit)
                    editPayload.ratesandfees.push(requestBody);
                else if(!requestBody.id)
                    newPayload.ratesandfees.push(requestBody);
            })
        } else if (this.state.activeTab.toUpperCase() === "AD VERIFICATION") {
            Object.keys(this.state.formData).forEach((key, keyIndex) => {
                let requestBody = {
                    account: this.state.formData[key].account,
                    country: this.state.formData[key].country,
                    created_by: this.state.formData[key].id ? this.state.formData[key].createdBy : this.props.omniProfile.email,
                    id: this.state.formData[key].id,
                    updated_by: this.state.formData[key].id ? this.props.omniProfile.email : this.state.formData[key].updatedBy,
                    partner: this.state.formData[key].partner,
                    format: this.state.formData[key].format,
                    rate: this.state.formData[key].rate,
                    effictive_date: this.state.formData[key].effectiveDate,
                    end_date: this.state.formData[key].endDate,
                }
                if(requestBody.id && this.state.formData[key].isEdit)
                    editPayload.ratesandfees.push(requestBody);
                else if(!requestBody.id)
                    newPayload.ratesandfees.push(requestBody);
            })
        }

        let requestArr = [];
        let successMessage = '';
        this.props.setSpinnerCount(1);
        if(newPayload.ratesandfees.length){
            let newRequest = RatesAndFeesService.createRatesAndFees(newPayload);
            requestArr.push(newRequest);
            successMessage+= 'added';
        }
        if(editPayload.ratesandfees.length){
            let editRequest = RatesAndFeesService.updateRatesAndFees(editPayload);
            requestArr.push(editRequest);
            successMessage+= '/modified';
        }
        Promise.all(requestArr).then((response)=>{
            if (this.state.activeTab === 'ad server') {
                this.setState({ spinnerCount: 0 }, () => {
                    this.createAdServerFormData();
                })
            } else if (this.state.activeTab === 'dsp') {
                this.setState({ spinnerCount: 0 }, () => {
                    this.createDspFormData();
                })
            } else if (this.state.activeTab === 'ad verification') {
                this.setState({ spinnerCount: 0 }, () => {
                    this.creteAdVerificationFormData();
                })
            }
            AlertMessagesService.showSuccessToastMessage(`Record has been ${successMessage} successfully`);
        }).catch((error)=>{
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.saveEdits, title: `Unable to ${successMessage} the record`, bodyMsgArr: []}]);
        }).finally(()=>{
            this.props.setSpinnerCount(0);
        })
    }

    cancelEdits = () => {
        let resetFormData = {};
        for(let i=0;i<this.state.data.length;i++){
            if (this.state.activeTab.toUpperCase() === "DSP") {
                resetFormData['row' + i] = this.createDSPData({}, this.state.data[i]);
            } else if (this.state.activeTab.toUpperCase() === "AD SERVER") {
                resetFormData['row' + i] = this.createAdServerData({}, this.state.data[i]);
                if(!['CPC', 'dCPC'].includes(resetFormData['row'+i]['clkRateType'])){
                    resetFormData['row'+i]['estimatedClicks'] = 'NA';
                }
            } else if (this.state.activeTab.toUpperCase() === "AD VERIFICATION") {
                resetFormData['row' + i] = this.createAdVerificationData({}, this.state.data[i]);
            }
            this.addNewAdServerColumn('clkRateType', this.createAdServerData({}, this.state.data[i]).clkRateType, resetFormData);
        }
        this.setState({formData : resetFormData, activeTabData: this.data, disableForm: true});
    }

    updateSearch = () => { }
    onSortUpdate = () => { }

    /**
     * The below code is a JavaScript code snippet. It is converting the first letter of each word in the string to uppercase in case 'isFirstLetter' is true 
     * else it converts the whole string to uppercase
     * @param {String} str: The string to be converted
     * @returns {String}: The converted string
     */
    convertStringToUpperCase = (str, isFirstLetter) => {
        if (isFirstLetter) {
            let strArr = str.split(' ');
            let convertedString = '';
            for (let i = 0; i < strArr.length; i++) {
                if (i === strArr.length - 1)
                    convertedString += strArr[i][0].toUpperCase() + strArr[i].slice(1);
                else
                    convertedString += strArr[i][0].toUpperCase() + strArr[i].slice(1) + ' ';
            }
            return convertedString;
        } else
            return str.toUpperCase();
    }

    /**
     * The below code is a JavaScript code snippet. It is setting the spinner count to 1 using the
     * `setSpinnerCount` function from the `props` object ans updates activeTab and mockData in state
     * @param {String} activeTab 
     */
    setActiveTab = (activeTab) => {
        this.props.setSpinnerCount(1);
        if (activeTab === 'ad server') {
            this.setState({ activeTab, formData: {}, activeTabData: [], loaded: false }, () => {
                this.createAdServerFormData();
            })
        } else if (activeTab === 'dsp') {
            this.setState({ activeTab, formData: {}, activeTabData: [], loaded: false }, () => {
                this.createDspFormData();
            })
        } else if (activeTab === 'ad verification') {
            this.setState({ activeTab, formData: {}, activeTabData: [], loaded: false }, () => {
                this.creteAdVerificationFormData();
            })
        }
    }

    /**
     * The below function will create a dropdown options object for the dropdowns in the form
     * from the default taxonomy data & rates&Fees dropdown API response and set in the state
    */
    createDropdownOptions = () => {
        let optionsObj = {}
        let fields = this.props.defaultTaxonomyData?.fields;
        for(let i=0;i<fields?.length;i++){
            if(fields[i].field_type === 1){
                let fieldName = fields[i].name.replace(" ", "").toLowerCase();
                let fieldOptions = [];
                for(let j=0;j<fields[i].fielditems.length;j++){
                    let option = {
                        label: fields[i].fielditems[j].name,
                        value: fields[i].fielditems[j].code
                    }
                    fieldOptions.push(option);
                }
                optionsObj[fieldName + "Options"] = fieldOptions;
            }
        }
        RatesAndFeesService.getRatesAndFeesDropdowns().then((response) => {
            response.forEach((item)=>{
                let options = item.values.map((value)=>{
                    if(value !== null && value !== '*'){
                        return {
                            label: value,
                            value: value
                        }
                    }
                })
                let fieldName = item.name.toLowerCase();
                if(!optionsObj[fieldName + "Options"]){
                    optionsObj[fieldName + "Options"] = options.filter((value)=>{
                        return value !== undefined
                    })
                }
            })
            this.setState({dropdownOptions: optionsObj})
        }).catch((error) => {
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.createDropdownOptions, title: 'Unable to create dropdown options', bodyMsgArr: []}]);
        })
    }

    createDSPData = (row, item) => {
        row['dsp'] = item ? item.dsp : "";
        row['country'] = item ? item.country : "Select";
        row['account'] = item ? item.account : "";
        row['dspCode'] = item ? item.code : "";
        row['buyType'] = item ? item.buy_type : "Select";
        row['platformFee'] = item ? item.platform_fee : "";
        row['type'] = item ? item.dsp_type : "";
        row['dataInFee'] = item ? item.data_in_fee : "";
        row['buyTypeDD'] = item ? item.buy_type_dd : "";
        row['createdBy'] = item ? item.created_by : "";
        row['updatedBy'] = item ? item.updated_by : "";
        row['effectiveDate'] = item ? (item.effictive_date.split("T").length ? item.effictive_date.split("T")[0] : item.effictive_date) : "";
        row['endDate'] = item ? (item.end_date.split("T").length ? item.end_date.split("T")[0] : item.end_date) : "";
        row['buyTypeCode'] = item ? item.buy_type_code : "";

        if(item){
            row['id'] = item.id;
            row['agencyId'] = item.agency_id;
            row['clientId'] = item.client_id;
            row['ratesAndFeesId'] = item.rates_fees_id;
        }
        return row;
    }


    /**
     * The below code is calling the ratesFees GET call for 'DSP' and creating a new object `row` for each
     * item in the array. It assigns values from the API response to properties of the `row` object. Then,
     * it adds the `row` object to a `formData` object using the index of the item as the key. 
     */
    createDspFormData = () => {
        let formData = {};
        let responseData = [];
        this.props.setSpinnerCount(1);
        RatesAndFeesService.getRatesAndFees('DSP').then((response) => {
            if (!response.length) {
                this.setState({ formData, activeTabData: responseData, loaded: true, disableForm: true, disableCancel: true }, () => {
                    this.data = responseData;
                    this.props.setSpinnerCount(0);
                });
            } else {
                response.forEach((item, index) => {
                    const row = {};
                    formData['row' + index] = this.createDSPData(row, item);
                    responseData.push(row);

                    if (index + 1 === response.length) {
                        this.setState({ formData, data: response, activeTabData: responseData, loaded: true, disableForm: true, disableCancel: true }, () => {
                            this.data = responseData;
                            this.props.setSpinnerCount(0);
                        });
                    }
                });
            }
            setTimeout(() => {
                let tableElement = document.getElementById('ratesAndFeesTable');
                if(tableElement) {
                    tableElement = tableElement.shadowRoot.querySelectorAll('.right');
                    tableElement && tableElement.forEach(item => item.style.height = '100%');
                }
            }, 10);
        }).catch((error) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.createDspFormData, title: 'Unable to create DSP form data', bodyMsgArr: []}]);
        });
    }

    createAdServerData = (row, item) => {
        row['adServer'] = item ? item.ad_server_name : "Select";
        row['country'] = item ? item.country : "Select";
        row['account'] = item ? item.account : "";
        row['adType'] = item ? item.ad_type : "";
        row['impRateType'] = item ? item.impression_rate_type : "Select";
        row['clkRateType'] = item ? item.click_rate_type : "Select";
        row['estimatedClicks'] = item ? item.fee_calc_type : "Select";
        row['currency'] = item ? item.currency : "Select";
        row['impRate'] = item ? item.impression_rate : "";
        row['clickRate'] = item ? item.click_rate : "";
        row['effectiveDate'] = item ? (item.effictive_date.split("T").length ? item.effictive_date.split("T")[0] : item.effictive_date) : "";
        row['endDate'] = item ? (item.end_date.split("T").length ? item.end_date.split("T")[0] : item.end_date) : "";
        row['createdBy'] = item ? item.created_by : "";
        row['updatedBy'] = item ? item.updated_by : "";

        if(item){
            row['id'] = item.id;
            row['agencyId'] = item.agency_id;
            row['clientId'] = item.client_id;
            row['ratesAndFeesId'] = item.rates_fees_id;
        }
        return row;
    }

    /**
     * The below code is calling the ratesFees GET call for 'Ad Server' and creating a new object `row` for each
     * item in the array. It assigns values from the API response to properties of the `row` object. Then,
     * it adds the `row` object to a `formData` object using the index of the item as the key. 
     */
    createAdServerFormData = () => {
        let formData = {};
        let responseData = [];
        this.props.setSpinnerCount(1);
        RatesAndFeesService.getRatesAndFees('Ad Server').then((response) => {
            if (!response.length) {
                this.setState({ formData, activeTabData: responseData, loaded: true, disableForm: true, disableCancel: true }, () => {
                    this.data = responseData;
                    this.props.setSpinnerCount(0);
                });
            } else {
                response.forEach((item, index) => {
                    const row = {};
                    formData['row' + index] = this.createAdServerData(row, item);
                    if(!['CPC', 'dCPC'].includes(formData['row'+index]['clkRateType'])){
                        formData['row'+index]['estimatedClicks'] = 'NA';
                    }
                    this.addNewAdServerColumn('clkRateType',this.createAdServerData(row, item).clkRateType, formData, false);
                    responseData.push(row);

                    if (index + 1 === response.length) {
                        this.setState({ formData, activeTabData: responseData, data: response, loaded: true, disableForm: true, disableCancel: true }, () => {
                            this.data = responseData;
                            this.props.setSpinnerCount(0);
                        });
                    }
                });
            }
            setTimeout(() => {
                let tableElement = document.getElementById('ratesAndFeesTable');
                if(tableElement) {
                    tableElement = tableElement.shadowRoot.querySelectorAll('.right');
                    tableElement && tableElement.forEach(item => item.style.height = '100%');
                }
            }, 10);
        }).catch((error) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.createAdServerFormData, title: 'Unable to create AD server data', bodyMsgArr: []}]);
        });
    }

    createAdVerificationData = (row, item) => {
        row['partner'] = item ? item.partner : "Select";
        row['country'] = item ? item.country : "Select";
        row['account'] = item ? item.account : "";
        row['format'] = item ? item.format : "Select";
        row['rate'] = item ? item.rate : "";
        row['effectiveDate'] = item ? (item.effictive_date.split("T").length ? item.effictive_date.split("T")[0] : item.effictive_date) : "";
        row['endDate'] = item ? (item.end_date.split("T").length ? item.end_date.split("T")[0] : item.end_date) : "";
        row['createdBy'] = item ? item.created_by : "";
        row['updatedBy'] = item ? item.updated_by : "";

        if(item){
            row['id'] = item.id;
            row['agencyId'] = item.agency_id;
            row['clientId'] = item.client_id;
            row['ratesAndFeesId'] = item.rates_fees_id;
        }
        return row;
    }

    /**
      * The below code is calling the ratesFees GET call for 'Ad Verification' and creating a new object `row` for each
      * item in the array. It assigns values from the API response to properties of the `row` object. Then,
      * it adds the `row` object to a `formData` object using the index of the item as the key. 
      */
    creteAdVerificationFormData = () => {
        let formData = {};
        let responseData = [];
        this.props.setSpinnerCount(1);
        RatesAndFeesService.getRatesAndFees('Ad Verification').then((response) => {
            if (!response.length) {
                this.setState({ formData, activeTabData: responseData, loaded: true, disableForm: true, disableCancel: true }, () => {
                    this.data = responseData;
                    this.props.setSpinnerCount(0);
                });
            } else {
                response.forEach((item, index) => {
                    const row = {};
                    formData['row' + index] = this.createAdVerificationData(row, item);
                    responseData.push(row);

                    if (index + 1 === response.length) {
                        this.setState({ formData, activeTabData: responseData, data: response, loaded: true, disableForm: true, disableCancel: true }, () => {
                            this.data = responseData;
                            this.props.setSpinnerCount(0);
                        });
                    }
                });
            }
            setTimeout(() => {
                let tableElement = document.getElementById('ratesAndFeesTable');
                if(tableElement) {
                    tableElement = tableElement.shadowRoot.querySelectorAll('.right');
                    tableElement && tableElement.forEach(item => item.style.height = '100%');
                }
            }, 10);
        }).catch((error) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.creteAdVerificationFormData, title: 'Unable to create AD Verification data', bodyMsgArr: []}]);
        });
    }

    /**
     * Adds a file upload event handler to a file input element with the ID 'rateFileInput'.
     * When a file is selected, this function reads the file's contents, processes it as an Excel spreadsheet,
     * and imports the data into the system as rate and fee information.
     */
    addFileUploadEventHandler = () => {
        const fileInput = document.getElementById('rateFileInput');

        fileInput.addEventListener('click', (event) => {
            event.stopImmediatePropagation();
            fileInput.value = null;
        })

        fileInput.addEventListener('change', (event) => {
            event.stopImmediatePropagation();
            const file = fileInput.files[0];
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array', cellDates: true, dateNF: 'yyyy-mm-dd' });

                let ratesFeesId = this.state.activeTabData.map(row => row.id);

                //payload for import rate and fees
                let rateFeesPayload = { type: this.convertStringToUpperCase(this.state.activeTab, this.state.activeTab !== 'dsp'), agency_id: '', ratesandfees: [] };
                for (let sheetName of workbook.SheetNames) {
                    let worksheet = workbook.Sheets[sheetName];
                    let sheetRows = XLSX.utils.sheet_to_json(worksheet, { raw: false });

                    // Filter out rows that don't have an agency ID & rows that already exist in the system
                    sheetRows = sheetRows.filter(row => row["agency_id"] && row["agency_id"] !== "" && !ratesFeesId.includes(Number(row.id)));

                    // Setting aggency id for payload
                    if (sheetRows[0] && sheetRows[0]["agency_id"]) {
                        rateFeesPayload["agency_id"] = sheetRows[0]["agency_id"];
                    }

                    // Remove the unwanted column from the rows and convert the platform fee to a number
                    sheetRows = sheetRows.map(row => {
                        delete row["id"];
                        delete row["agency_id"];
                        delete row["client_id"]
                        delete row["rates_fees_id"]
                        delete row["type"]

                        if (row["platform_fee"]) {
                            row["platform_fee"] = Number(row["platform_fee"]);
                        }
                        row["updated_by"] = "";
                        row["created_by"] = this.props?.omniProfile?.email;
                        return row;
                    });

                    // Add the rows to the payload
                    rateFeesPayload["ratesandfees"] = rateFeesPayload["ratesandfees"].concat(sheetRows);
                }
                this.setState({ spinnerCount: 1 });

                // Send the rate and fee data for import
                RatesAndFeesService.createRatesAndFees(rateFeesPayload)
                    .then((response) => {
                        AlertMessagesService.showSuccessToastMessage('Rate and fees are Imported!');
                        if (this.state.activeTab === 'dsp') {
                            this.createDspFormData();
                        } else if (this.state.activeTab === 'ad server') {
                            this.createAdServerFormData();
                        } else if (this.state.activeTab === 'ad verification') {
                            this.creteAdVerificationFormData();
                        }
                        this.setState({ spinnerCount: 0 });
                    }, (error) => {
                        this.setState({ spinnerCount: 0 });
                        if (error.code === "ERR_BAD_RESPONSE")
                            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.addFileUploadEventHandler, title: 'Unable to upload the file', bodyMsgArr: []}]);
                        else
                            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.addFileUploadEventHandler, title: 'Unable to upload the file', bodyMsgArr: []}]);
                    });
            };
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * Handles the click event for importing or exporting data based on the provided label.
     * @param {Event} event - The event object containing details about the click event.
     */
    importExportClick = (event) => {
        const label = event?.detail?.value?.label;
        let adType = this.convertStringToUpperCase(this.state.activeTab, this.state.activeTab !== 'dsp');

        if (label.includes('Export')) {
            this.setState({ spinnerCount: 1 });
            // Call the RatesAndFeesService to export rate and fee data
            RatesAndFeesService.getRatesAndFees(adType)
                .then((response) => {
                    AlertMessagesService.showSuccessToastMessage(`${adType} rates and fees are exported successfully!`);
                    // Reorder the object keys to match the Excel file headers
                    response = response.map((row) => {
                            const reorderedObject = {};
                            // Add the fee_calc_type i.e., click_rate_type to the object as fee_calc_type in export report
                            Object.keys(row).forEach(key => {
                              reorderedObject[key] = row[key];
                              if (key === 'click_rate_type') {
                                reorderedObject['fee_calc_type'] = row['fee_calc_type'];
                              }
                            });
                            return reorderedObject;
                    })
                    // Export the data to an Excel file using the utils function
                    Utils.exportToExcel(response, 'ratesAndFees', adType + '_RatesAndFees.xlsx');
                    this.setState({ spinnerCount: 0 });
                }, (error) => {
                    AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.importExportClick, title: `Unable to export the ${adType} rates and fees`, bodyMsgArr: []}]);
                    this.setState({ spinnerCount: 0 });
                });
        } else if (label.includes('Import')) {
            // Trigger a click event on the file input element to initiate file selection
            document.getElementById('rateFileInput').click();
        }
    }

    /**
     * Handles the click event for the 'Delete' button.
     * @param {Object} value - The object containing details about the row to be deleted.
     * @param {Number} index - The index of the row to be deleted.
    */
    deleteRow = (value, index) => {
        this.setState({ isAlertOpen: true, deleteData: value, deleteIndex: index });
    }

    /**
     * Handles the click event for the 'Confirm' button on the delete confirmation alert.
     * This will call the delete API to delete the selected rate and fee.
    */
    confirmDelete = () => {
        let deleteId = this.state.deleteData.id;
        if(deleteId){
            this.setState({ isAlertOpen: false, spinnerCount: 1 });
            RatesAndFeesService.deleteRatesAndFees(deleteId)
            .then((response) => {
                if (this.state.activeTab === 'ad server') {
                    this.setState({ spinnerCount: 0 }, () => {
                        this.createAdServerFormData();
                    })
                } else if (this.state.activeTab === 'dsp') {
                    this.setState({ spinnerCount: 0 }, () => {
                        this.createDspFormData();
                    })
                } else if (this.state.activeTab === 'ad verification') {
                    this.setState({ spinnerCount: 0 }, () => {
                        this.creteAdVerificationFormData();
                    })
                }
                AlertMessagesService.showSuccessToastMessage('Selected Rate and fees is deleted!');
            }, (error) => {
                this.setState({ spinnerCount: 0, deleteData: {} });
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.confirmDelete, title: 'Unable to delete the selected Rate and Fees', bodyMsgArr: []}]);
            });
        } else {
            let currentData = [...this.state.activeTabData];
            currentData.splice(this.state.deleteIndex, 1);
            let formData = {};
            for(let i=0;i<currentData.length;i++){
                formData['row' + i] = currentData[i];
            }
            this.setState({ isAlertOpen: false, formData, activeTabData: currentData });
        }
    }

    displaySkeletonTable() {
        let rows = '';
        for (let i = 0; i < 9; i++) {
            rows = rows + '<div className="table skeleton"></div>';
        }
        rows = (html`${rows}`);
        return <>{parse(rows.values[0])}</>;
    }

    /**
     * Function to handle the page resize event on the placement table to load more data and set the height of the tile
     * @param {Object} event - Event object
     * @returns {void}
    */ 
    ontableResize = (event) => {
        //Set height of the tile based on the window height
        let windowHeight = window.innerHeight;
        if(windowHeight - 150 > 0){
            document.getElementsByClassName("rate-fee-tile")[0].style.maxHeight = windowHeight - 200 + 'px';
        }
    }

    render() {
        return (<>
            <div className="ratesAndFeesContainer">
                <div>
                    <input type="file" id="rateFileInput" accept=".xlsx, .xls" hidden />
                    <LoaderComponent spinnerCount={this.state.spinnerCount} displayWholePage={true} />
                        <OmniTileComponent className="rate-fee-tile" scrollheading="">
                            <OmniToolbarComponent slot="header">
                                <div slot="start" className="table-titles">
                                    <button className={`${(this.state.activeTab === 'dsp') ? '' : 'secondary'}`} onClick={() => this.setActiveTab('dsp')}>DSP</button>
                                    <button className={`${(this.state.activeTab === 'ad server') ? '' : 'secondary'}`} onClick={() => this.setActiveTab('ad server')}>Ad Server</button>
                                    <button className={`${(this.state.activeTab === 'ad verification') ? '' : 'secondary'}`} onClick={() => this.setActiveTab('ad verification')}>Ad Verification</button>
                                </div>

                                <div slot="end" className="custom-dropdown-menu">
                                    <button className="secondary marginRight20" onClick={this.addRow}>Add row</button>
                                    <OmniIconMenu
                                        options={this.importExportOption()}
                                        iconId="omni:interactive:importExport"
                                        iconClass="is-size-4"
                                        tooltip="Import/Export"
                                        onDropdownChange={(event) => this.importExportClick(event)}>
                                    </OmniIconMenu>
                                    <OmniSearchComponent className="searchIcon" onSearchUpdate={this.updateSearch} slot="end" ph="Search"></OmniSearchComponent>
                                    <div className="Line-Copy-2"></div>
                                    <OmniIconComponent className="is-size-4" icon-id="omni:interactive:filter"></OmniIconComponent>
                                </div>
                            </OmniToolbarComponent>
                        

                            <div className="table-section">
                            {(this.state.loaded && this.state.activeTab === 'dsp' && this.state.activeTabData.length > 0) && <OmniTableStickyComponent
                                stickyside="right"
                                id="ratesAndFeesTable"
                                data={this.state.activeTabData}
                                leftColumns={this.dspLeftColumns}
                                rightColumns={this.dspRightColumns}
                                onSortUpdate={this.onSortUpdate}
                                key={this.state.activeTabData.length}
                                keyFn={row => row.id}
                                stickyscrollbar={true}>
                            </OmniTableStickyComponent>}

                            {(this.state.loaded && this.state.activeTab === 'ad server' && this.state.activeTabData.length > 0) && <OmniTableStickyComponent
                                stickyside="right"
                                id="ratesAndFeesTable"
                                data={this.state.activeTabData}
                                leftColumns={this.adServerLeftColumns}
                                rightColumns={this.adServerRightColumns}
                                onSortUpdate={this.onSortUpdate}
                                key={this.state.activeTabData.length}
                                keyFn={row => row.id}>
                            </OmniTableStickyComponent>}

                            {(this.state.loaded && this.state.activeTab === 'ad verification' && this.state.activeTabData.length > 0) && <OmniTableStickyComponent
                                stickyside="right"
                                id="ratesAndFeesTable"
                                data={this.state.activeTabData}
                                leftColumns={this.adVerificationLeftColumns}
                                rightColumns={this.adVerificationRightColumns}
                                onSortUpdate={this.onSortUpdate}
                                key={this.state.activeTabData.length}
                                keyFn={row => row.id}>
                            </OmniTableStickyComponent>}

                            {(this.state.loaded && this.state.activeTabData.length === 0) && <div style={{minHeight: "40vh"}}><NoDataComponent
                                heading={"No Data Found"}
                                message={`No ${this.state.activeTab} found, Please create a new one`}
                            /></div>
                            }

                            {(!this.state.loaded && this.state.activeTabData.length === 0) && this.displaySkeletonTable()}
                            </div>

                        </OmniTileComponent>
                        <div className="footer-buttons">
                            <button className="save-edit-button" disabled={this.state.disableForm} onClick={this.saveEdits}>Save</button>
                            <button className="tertiary" disabled={this.state.disableCancel} onClick={this.cancelEdits}>Cancel</button>
                        </div>
                        <div className={`modal ${this.state.isAlertOpen ? 'is-active' : ''}`}>
                            <div className="modal-background is-warning"></div>
                            <div className="modal-content">
                                <article className="message is-warning">
                                    <div className="message-header">
                                        <p>
                                            <omni-icon icon-id="omni:informative:alert"></omni-icon>
                                            Delete row
                                        </p>
                                    </div>
                                    <div className="message-body">
                                        <div>This action would permanently delete selected data row.</div>
                                        <div>How would you like to proceed?</div>
                                    </div>
                                    <div className="message-footer">
                                        <div className="field is-grouped is-grouped-right">
                                            <div className="control">
                                                <button
                                                    className="tertiary warning"
                                                    onClick={() => { this.setState({ isAlertOpen: false }) }}>Cancel</button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className="secondary warning"
                                                    onClick={() => this.confirmDelete()}>Delete</button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </div>
                </div>
            </div>
        </>)
    }
}